import { useState, useEffect, useRef } from 'react';
import useAuth from '../../hooks/useAuth';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGlobalState } from '../../contexts/GlobalStateContext';
import paises from '../../paises.json';
import ScrolledHeader from './ScrolledHeader';
import './WhiteHeader.css';
import logo from '../../assets/jovilogo.png';

import CurrencyDropdown from '../../components/CurrencyDropdown.jsx';
import { FaHeart, FaBalanceScale } from 'react-icons/fa';
import { useFavoritesCount } from '../../hooks/useFavoritesCount';

const WhiteHeader = () => {
  const { isLoggedIn, user, logout } = useAuth();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const dropdownBtnStyle = { width: '100%', padding: '10px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 15 };
  const favCount = useFavoritesCount();
  const [cmpCount, setCmpCount] = useState(0);
  const currencyRefDesktop = useRef(null);
  const currencyRefMobile = useRef(null);

  // Carrega contador de comparação do localStorage
  useEffect(() => {
    const loadCompareCount = () => {
      try {
        const cmps = JSON.parse(localStorage.getItem('compare_items') || '[]');
        setCmpCount(Array.isArray(cmps) ? cmps.length : 0);
      } catch {
        setCmpCount(0);
      }
    };
    loadCompareCount();
    const onStorage = (e) => {
      if (e.key === 'compare_items') loadCompareCount();
    };
    const onFocus = () => loadCompareCount();
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // Fecha dropdown de moeda ao clicar/pressionar fora
  useEffect(() => {
    const handleOutside = (event) => {
      const hitDesktop = currencyRefDesktop.current && currencyRefDesktop.current.contains(event.target);
      const hitMobile = currencyRefMobile.current && currencyRefMobile.current.contains(event.target);
      if (!hitDesktop && !hitMobile) {
        const evt = new CustomEvent('close-currency-dropdown', { bubbles: true });
        if (currencyRefDesktop.current) currencyRefDesktop.current.dispatchEvent(evt);
        if (currencyRefMobile.current) currencyRefMobile.current.dispatchEvent(evt);
      }
    };
    document.addEventListener('pointerdown', handleOutside, true);
    document.addEventListener('click', handleOutside, true);
    document.addEventListener('touchstart', handleOutside, true);
    return () => {
      document.removeEventListener('pointerdown', handleOutside, true);
      document.removeEventListener('click', handleOutside, true);
      document.removeEventListener('touchstart', handleOutside, true);
    };
  }, []);

  // Dropdown handlers
  const toggleDropdown = () => setIsDropdownOpen((v) => !v);
  const closeDropdown = () => setIsDropdownOpen(false);
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    }
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  const handleDashboard = () => {
    closeDropdown();
    window.location.href = '/dashboard';
  };
  const handleProfile = () => {
    closeDropdown();
    window.location.href = '/dashboard/profile';
  };
  const handleLogout = () => {
    try {
      const path = location.pathname || '/';
      if (!path.startsWith('/dashboard')) {
        localStorage.setItem('post_login_redirect', path + location.search);
      }
    } catch {
      // ignore
    }
    logout();
    closeDropdown();
    window.location.href = '/';
  };

  // Abre login salvando retorno
  const openLogin = () => {
    try { localStorage.setItem('post_login_redirect', location.pathname + location.search); } catch {
      // ignore
    }
    try { handleOpenLogin(); } catch {
      // ignore
    }
  };

  const { t, i18n } = useTranslation();
  const location = useLocation();
  // Proteção temporária: não quebre se contexto global não existir
  const globalState = useGlobalState();
  const handleOpenLogin = globalState?.handleOpenLogin || (() => {});
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentCountry, setCurrentCountry] = useState('br');
  const [searchTerm, setSearchTerm] = useState('');

  const dedupByCode = (list) => {
    const seen = new Set();
    return list.filter((p) => {
      const key = String(p?.code || '').toUpperCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // Filtrar países baseado no termo de busca
  const filtrarPaises = () => {
    if (!searchTerm) {
      return Object.fromEntries(
        Object.entries(paises.continents).map(([regiao, paisesDaRegiao]) => [regiao, dedupByCode(paisesDaRegiao)])
      );
    }

    const termoBusca = searchTerm.toLowerCase();
    const regioesFiltradas = {};

    Object.entries(paises.continents).forEach(([regiao, paisesDaRegiao]) => {
      const paisesFiltrados = dedupByCode(
        paisesDaRegiao.filter(pais => 
          pais.nome.toLowerCase().includes(termoBusca) ||
          pais.idioma.toLowerCase().includes(termoBusca) ||
          pais.moeda.toLowerCase().includes(termoBusca)
        )
      );

      if (paisesFiltrados.length > 0) {
        regioesFiltradas[regiao] = paisesFiltrados;
      }
    });

    return regioesFiltradas;
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  const toggleLanguageModal = () => {
    setIsLanguageModalOpen(!isLanguageModalOpen);
  };

  // Resolve o código de idioma suportado pelo i18n a partir do país/idioma selecionado
  const resolveLanguage = (pais) => {
    const code = (pais?.code || '').toUpperCase();
    const idioma = (pais?.idioma || '').toLowerCase();
    if (idioma.includes('portugu')) return code === 'BR' ? 'pt-BR' : 'pt-PT';
    if (idioma.includes('english') || idioma.includes('ingl')) return 'en-US';
    // Fallback por país
    const ptCountries = ['BR','PT','MZ','GW','AO','CV','ST','TL'];
    if (ptCountries.includes(code)) return code === 'BR' ? 'pt-BR' : 'pt-PT';
    const enCountries = ['US','GB','AU','NZ','CA','IE'];
    if (enCountries.includes(code)) return 'en-US';
    return 'en-US';
  };

  const handleLanguageChange = (pais) => {
    const lang = resolveLanguage(pais);
    i18n.changeLanguage(lang);
    try {
      localStorage.setItem('lang', lang);
      localStorage.setItem('country', (pais.code || '').toLowerCase());
    } catch {
      // ignore
    }
    setCurrentCountry((pais.code || '').toLowerCase());
    setIsLanguageModalOpen(false);
  };

  // Mantém bandeira e idioma sincronizados ao carregar/atualizar
  useEffect(() => {
    try {
      const savedCountry = localStorage.getItem('country');
      const langNow = String(i18n.language || '').trim();
      if (savedCountry) {
        setCurrentCountry(savedCountry);
      } else if (langNow) {
        const map = { 'pt-PT': 'pt', 'pt-BR': 'br', 'en-US': 'us', 'es-ES': 'es', 'fr-FR': 'fr' };
        setCurrentCountry(map[langNow] || 'us');
      }
    } catch {
      // ignore
    }
  }, [i18n.language]);

  // Quando o idioma mudar em qualquer lugar (bootstrap/domínio/scroll header), sincroniza a bandeira
  useEffect(() => {
    const map = { 'pt-PT': 'pt', 'pt-BR': 'br', 'en-US': 'us', 'es-ES': 'es', 'fr-FR': 'fr' };
    const handler = (lng) => {
      try {
        const next = map[String(lng || '').trim()] || 'us';
        setCurrentCountry(next);
      } catch {
      // ignore
    }
    };
    try {
      i18n.on('languageChanged', handler);
    } catch {
      // ignore
    }
    return () => {
      try { i18n.off('languageChanged', handler); } catch {
        // ignore
      }
    };
  }, [i18n]);

  // Redireciona de volta após login (se houver post_login_redirect)
  useEffect(() => {
    if (isLoggedIn) {
      try {
        const back = localStorage.getItem('post_login_redirect');
        if (back) {
          localStorage.removeItem('post_login_redirect');
          navigate(back);
        }
      } catch {
      // ignore
    }
    }
  }, [isLoggedIn, navigate]);

  // Se estiver com scroll, usa o ScrolledHeader
  if (isScrolled) {
    return <ScrolledHeader />;
  }

  return (
    <>
    <header className="header white">
      <div className="header-content">
        <Link to="/" className="logo">
          <img src={logo} alt="JoviPlanet" />
        </Link>

        <nav className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
          <ul>
            <li>
              <Link to="/about" className={location.pathname === '/about' ? 'active' : ''}>
                {t('header.about', { defaultValue: 'Sobre nós' })}
              </Link>
            </li>
           {/*  <li>
              <Link to="/plans" className={location.pathname === '/plans' ? 'active' : ''}>
                Preços
              </Link>
            </li> */}
            <li>
              <Link to="/contact" className={location.pathname === '/contact' ? 'active' : ''}>
                {t('header.contact', { defaultValue: 'Contato' })}
              </Link>
            </li>
          </ul>
        </nav>

        <div className={`header-actions ${isMenuOpen ? 'active' : ''}`}>
          {(favCount > 0 || cmpCount > 0) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginRight: 8 }}>
              {favCount > 0 && (
                <button
                  type="button"
                  onClick={() => navigate('/favorites')}
                  title={t('common.favorites', { defaultValue: 'Favoritos' })}
                  style={{ position: 'relative', border: 'none', background: 'transparent', cursor: 'pointer' }}
                >
                  <FaHeart style={{ color: '#BA824F', fontSize: 20 }} />
                  <span style={{ position: 'absolute', top: -6, right: -8, background: '#BA824F', color: '#fff', borderRadius: 10, padding: '0 6px', fontSize: 11, lineHeight: '18px', minWidth: 18, textAlign: 'center' }}>{favCount}</span>
                </button>
              )}
              {cmpCount > 0 && (
                <button
                  type="button"
                  onClick={() => navigate('/compare')}
                  title={t('common.compare', { defaultValue: 'Comparar imóveis' })}
                  style={{ position: 'relative', border: 'none', background: 'transparent', cursor: 'pointer' }}
                >
                  <FaBalanceScale style={{ color: '#1f2937', fontSize: 20 }} />
                  <span style={{ position: 'absolute', top: -6, right: -8, background: '#111827', color: '#fff', borderRadius: 10, padding: '0 6px', fontSize: 11, lineHeight: '18px', minWidth: 18, textAlign: 'center' }}>{cmpCount}</span>
                </button>
              )}
            </div>
          )}
          <button 
            className="flag-button"
            onClick={toggleLanguageModal}
          >
            <img
              src={`/flags/${currentCountry}.png`}
              alt={t('header.selectedCountry', { defaultValue: 'País selecionado' })}
            />
          </button>

          <div className="currency-selector" ref={currencyRefDesktop}>
            <CurrencyDropdown />
          </div>

          {isLoggedIn ? (
            <div className="user-dropdown-wrapper" ref={dropdownRef} style={{ position: 'relative', display: 'inline-block', marginLeft: 6 }}>
              <button className="user-button" onClick={toggleDropdown} style={{ padding: '10px 16px', borderRadius: 8, background: '#f3f3f3', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>{user?.nome || t('common.user', { defaultValue: 'Usuário' })}</span>
                <span style={{ fontSize: 12 }}>{isDropdownOpen ? '▲' : '▼'}</span>
              </button>
              {isDropdownOpen && (
                <div className="user-dropdown" style={{ position: 'absolute', right: 0, top: '110%', background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', borderRadius: 8, zIndex: 999, minWidth: 180 }}>
                  <button onClick={handleDashboard} style={dropdownBtnStyle}>{t('common.dashboard')}</button>
                  <button onClick={handleProfile} style={dropdownBtnStyle}>{t('common.openProfile', { defaultValue: 'Abrir Perfil' })}</button>
                  <button onClick={handleLogout} style={dropdownBtnStyle}>{t('common.logout')}</button>
                </div>
              )}
            </div>
          ) : (
            <button onClick={openLogin} className="login-button">
              {t('header.login', { defaultValue: 'Login' })}
            </button>
          )}

          <Link to="/plans" className="host-button" style={{ marginLeft: 0 }}>
            {t('header.becomeHost', { defaultValue: 'Torne-se um anfitrião' })}
          </Link>
        </div>
        {/* Seletor de moeda dedicado para mobile, visível ao lado do menu */}
        <div className="currency-selector-mobile" ref={currencyRefMobile}>
          <CurrencyDropdown />
        </div>

        <button 
          className={`menu-toggle ${isMenuOpen ? 'active' : ''}`}
          onClick={toggleMenu}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </header>

      {isLanguageModalOpen && (
        <div className="language-modal-overlay" onClick={toggleLanguageModal}>
          <div className="language-modal" onClick={e => e.stopPropagation()}>
            <div className="language-modal-header">
              <h2>{t('header.select_country', { defaultValue: 'Selecione seu país' })}</h2>
              {/* Use i18n to allow fallback translation */}
              {/* <h2>{t('header.select_country', { defaultValue: 'Selecione seu país' })}</h2> */}
              <button className="close-button" onClick={toggleLanguageModal}>
                <span>×</span>
              </button>
            </div>
            <div className="language-search">
              <input
                type="text"
                placeholder={t('header.search_country', { defaultValue: 'Buscar país...' })}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="language-list">
              {Object.entries(filtrarPaises()).map(([regiao, paises]) => (
                <div key={regiao} className="language-region">
                  <h3>{regiao}</h3>
                  <div className="language-buttons">
                    {paises.map((pais) => (
                      <button
                        key={`${pais.code}-${pais.idioma}`}
                        className={`language-button ${currentCountry === pais.code.toLowerCase() ? 'active' : ''}`}
                        onClick={() => handleLanguageChange(pais)}
                      >
                        <img
                          src={`/flags/${pais.code.toLowerCase()}.png`}
                          alt={pais.nome}
                        />
                        <span>{pais.nome}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WhiteHeader;